<script setup lang="ts">
// @ts-expect-error Not a ts file
import store from '@/store'
import { ITicketPurchased } from '@/features/tickets/types'
import { useUsersInfiniteQuery } from '@/enitites/user/api/queries'
import { computed, ref, watch } from 'vue'
import { UserDB, UsersDBSearchParams } from '@/enitites/user'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { useImage } from '@/shared/composables/image'
import useValidationRules from '@/shared/composables/validation'
import { useAssignTicketToUserMutation } from '@/features/tickets/api/queries'
import BKInput from '@/components/AppShared/BKInput.vue'

const props = defineProps<{
  ticket: ITicketPurchased
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'successful-assignment'): void
}>()

const params = ref<UsersDBSearchParams>({
  pageNumber: 0,
  pageSize: 50,
  keyword: '',
})

const { getImageById } = useImage()
const rules = useValidationRules()
const getUserDB = computed(() => store.getters.getUserDB)
const { data, isFetching: isLoadingUsers } = useUsersInfiniteQuery(params.value)
const { mutate, isPending: isAssigningTicket, error: assignmentError } = useAssignTicketToUserMutation()

const users = computed(() => (data.value ? data.value.pages.flatMap((page) => page) : []))
const selectedUser = ref<Partial<UserDB> | string | null>(props.ticket.assignedTo ? props.ticket.assignedTo : null)
const fullNameForEmail = ref<string>('')
const isUserChanged = computed(() => {
  if (props.ticket.assignedTo && selectedUser.value && typeof selectedUser.value === 'object') {
    return props.ticket.assignedTo.id !== selectedUser.value.id
  }
  if (typeof selectedUser.value === 'string') {
    return props.ticket.assignedTo?.email !== selectedUser.value
  }
  return selectedUser.value !== props.ticket.assignedTo
})

const validate = (value: unknown) => {
  if (typeof value === 'string') {
    return rules.email(value)
  }
  if (value && typeof value === 'object' && ('id' in value || 'userId' in value)) {
    return true
  }
  return 'Please select a valid user or enter a valid email address.'
}

const getAgeByBirthdate = (birthdate: string): number => {
  const today = new Date()
  const birthDate = new Date(birthdate)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

const handleConfirmAssignment = () => {
  if (!isUserChanged.value || !selectedUser.value || isAssigningTicket.value) return

  const assignedToId =
    typeof selectedUser.value === 'object' && 'id' in selectedUser.value && selectedUser.value.id
      ? selectedUser.value.id
      : undefined
  const assignedToEmail = typeof selectedUser.value === 'string' ? selectedUser.value : undefined

  mutate(
    {
      ticketId: props.ticket.id,
      userId: assignedToId,
      email: assignedToEmail,
      fullName: assignedToEmail ? fullNameForEmail.value : undefined,
    },
    {
      onSuccess: () => {
        emit('successful-assignment')
        emit('close')
      },
    }
  )
}

const updateUserSelectorKey = ref(0)
const handleSelectedUserChange = () => {
  updateUserSelectorKey.value += 1
}

watch(
  () => props.ticket,
  () => {
    selectedUser.value = props.ticket.assignedTo ? props.ticket.assignedTo : null
    fullNameForEmail.value = ''
  }
)
</script>

<template>
  <div class="ticket-assignment-interface">
    <div class="content">
      <p class="content-title">Assign to</p>

      <div class="select-wrapper">
        <v-combobox
          :key="updateUserSelectorKey"
          v-model="selectedUser"
          append-icon=""
          class="user-selector"
          color="var(--color-dark-800)"
          eager
          height="56"
          hide-details="auto"
          :items="users"
          item-color="var(--color-dark-800)"
          item-value="id"
          item-text="dancerName"
          :loading="isLoadingUsers"
          :menu-props="{
            attach: '.select-wrapper',
            contentClass: 'user-select-menu',
            eager: true,
            maxHeight: 279,
          }"
          no-filter
          outlined
          placeholder="Search users by name or email"
          return-object
          :rules="[validate]"
          :search-input.sync="params.keyword"
          validate-on-blur
          @change="handleSelectedUserChange"
        >
          <template #selection="{ item }">
            <div
              v-if="typeof item === 'object' && item.dancerName"
              class="selected-item"
            >
              <AvatarPlaceholder
                v-if="!item.avatar"
                background-color="#DDDDDD"
                color="#333333"
                size="24"
              />
              <v-avatar
                v-else
                size="24"
              >
                <v-img :src="getImageById(item.avatar, 48, 48, 'avatar')" />
              </v-avatar>
              <span>
                {{ item.dancerName }} <span class="hint-mark">{{ getUserDB.id === item.userId ? '(You)' : '' }}</span>
              </span>
            </div>
            <span
              v-else
              class="selected-item"
            >
              <AvatarPlaceholder
                background-color="#DDDDDD"
                color="#333333"
                size="24"
              />
              {{ typeof item === 'string' ? item : item.email ? item.email : 'Unassigned' }}
            </span>
          </template>
          <template #no-data>
            <div class="no-data-item">
              <v-progress-circular
                v-if="isLoadingUsers"
                indeterminate
                color="var(--color-blue-800)"
              />
              <span v-else>
                <b>No users found</b><br />
                If the user doesn’t have a profile, use their email to send and assign ticket.
              </span>
            </div>
          </template>
          <template #item="{ item }">
            <div class="select-item">
              <AvatarPlaceholder
                v-if="!item.avatar"
                background-color="#DDDDDD"
                color="#333333"
              />
              <v-avatar
                v-else
                size="20"
              >
                <v-img :src="getImageById(item.avatar, 48, 48, 'avatar')" />
              </v-avatar>
              <span> {{ item.dancerName }} {{ getUserDB.id === item.userId ? '(You)' : '' }} </span>
            </div>
          </template>
        </v-combobox>
      </div>

      <div
        v-if="isUserChanged && selectedUser && typeof selectedUser === 'object'"
        class="selected-user-card"
      >
        <div class="selected-user">
          <v-avatar size="100">
            <v-img :src="getImageById(selectedUser.avatar || '', 200, 200, 'avatar')" />
          </v-avatar>

          <div class="selected-user-info">
            <p class="selected-user-dancer-name">
              {{ selectedUser.dancerName }}
              <span v-if="getUserDB.id === selectedUser.id">(You)</span>
            </p>
            <p class="selected-user-full-name">
              {{ selectedUser.fullName
              }}{{ selectedUser.birthday ? `, ${getAgeByBirthdate(selectedUser.birthday)}` : '' }}
            </p>
            <p class="selected-user-location">
              {{ selectedUser.city }}{{ selectedUser.state ? ` ${selectedUser.state}` : ''
              }}{{ selectedUser.country ? `, ${selectedUser.country}` : '' }}
            </p>
          </div>
        </div>
        <div class="confirmation-message">
          <strong>Make sure</strong> this is the profile you want to assign the ticket to
        </div>
      </div>

      <div
        v-if="isUserChanged && typeof selectedUser === 'string'"
        class="selected-user-email"
      >
        <div class="confirmation-message">
          <b>Can’t find a profile for this email.</b> We’ll use email to assign and send ticket.
        </div>

        <BKInput
          v-model="fullNameForEmail"
          placeholder="Add recipient’s full name"
          :rules="[rules.required]"
        />
      </div>

      <p
        v-if="!isUserChanged"
        class="hint"
      >
        Find a profile with a username or email. <strong>If the user doesn’t have a profile</strong> use their email to
        send and assign ticket.
      </p>
    </div>

    <div
      v-if="assignmentError"
      class="error-message"
    >
      {{ assignmentError.message }}
    </div>

    <div class="actions">
      <BKButton
        class="action-button"
        outlined
        @click="$emit('close')"
      >
        Cancel
      </BKButton>
      <BKButton
        class="action-button"
        :disabled="!isUserChanged || (typeof selectedUser === 'string' && !fullNameForEmail.trim())"
        :loading="isAssigningTicket"
        white-text
        @click="handleConfirmAssignment"
      >
        Confirm Assignment
      </BKButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ticket-assignment-interface {
  display: flex;
  flex-direction: column;
  gap: 40px;
  justify-content: space-between;
  height: 100%;

  p {
    margin: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .content-title {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);
    }

    .hint {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-dark-800);
    }
  }

  .select-wrapper {
    position: relative;

    .selected-item {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
      display: flex;
      align-items: center;
      gap: 4px;

      .hint-mark {
        color: var(--color-grey-300);
      }
    }

    .user-selector {
      border-radius: 6px;
      border-color: var(--color-grey-300);

      :deep(.v-input__slot) {
        background-color: var(--color-white);
      }

      &.v-select--is-menu-active {
        border-radius: 6px 6px 0 0;
        border: 1px solid var(--color-grey-300) !important;

        :deep(*) {
          border: none !important;
        }

        .selected-item {
          display: none;
        }
      }
    }

    .select-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
    }

    .no-data-item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
    }
  }

  .selected-user-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 38px;

    .selected-user {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px;

      .selected-user-info {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .selected-user-dancer-name {
          font-weight: 500;
          font-size: 20px;
          line-height: 1.3;
          color: var(--color-dark-800);
          margin: 0;
        }

        .selected-user-full-name,
        .selected-user-location {
          font-size: 14px;
          line-height: 20px;
          color: var(--color-grey-300);
          margin: 0;
        }
      }
    }

    .confirmation-message {
      max-width: 220px;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);
      text-align: center;
      text-wrap: balance;
    }
  }

  .selected-user-email {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .confirmation-message {
      font-size: 12px;
      line-height: 14px;
      color: var(--color-dark-800);
    }
  }

  .error-message {
    margin-top: auto;
    color: var(--error-red);
    font-size: 14px;
    line-height: 20px;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;

    .action-button {
      flex: 1 1 0;
      min-width: fit-content;
    }
  }
}

:deep(.user-select-menu) {
  position: static;
  border-radius: 0 0 6px 6px;
  border: 1px solid var(--color-grey-300);
  border-top: none;
  box-shadow: none;
  min-width: 100% !important;

  .v-list-item {
    padding: 0 12px;
  }
}
</style>
